import { useCallback, useMemo, useState } from 'react';
import { BodyType, Car, CatalogFilterState, SortOption } from '../types/car';

export type ModalType = 'finance' | 'tradein' | 'inquiry' | 'compare';

export const MAX_COMPARE = 3;

export const emptyFilters: CatalogFilterState = {
  brand: '',
  model: '',
  version: '',
  yearFrom: '',
  yearTo: '',
  maxPrice: '',
  fuel: '',
  transmission: '',
  condition: '',
};

function matchesFilters(c: Car, f: CatalogFilterState): boolean {
  if (f.brand && c.brand !== f.brand) return false;
  if (f.model && c.model !== f.model) return false;
  if (f.version && c.version !== f.version) return false;
  if (f.yearFrom && c.year < Number(f.yearFrom)) return false;
  if (f.yearTo && c.year > Number(f.yearTo)) return false;
  if (f.maxPrice && c.cashPrice > Number(f.maxPrice)) return false;
  if (f.fuel && c.fuel !== f.fuel) return false;
  if (f.transmission && c.transmission !== f.transmission) return false;
  if (f.condition && c.condition !== f.condition) return false;
  return true;
}

export function useCatalog(cars: Car[]) {
  const [query, setQuery] = useState('');
  const [bodyType, setBodyType] = useState<BodyType | 'Todos'>('Todos');
  const [sort, setSort] = useState<SortOption>('relevance');
  const [filters, setFilters] = useState<CatalogFilterState>(emptyFilters);
  const [compareIds, setCompareIds] = useState<string[]>([]);
  const [modalType, setModalType] = useState<ModalType | null>(null);
  const [activeCar, setActiveCar] = useState<Car | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = cars.filter(
      (c) =>
        (bodyType === 'Todos' || c.bodyType === bodyType) &&
        (!q || `${c.brand} ${c.model} ${c.version}`.toLowerCase().includes(q)) &&
        matchesFilters(c, filters),
    );
    const sorted = [...list];
    if (sort === 'price-asc') sorted.sort((a, b) => a.cashPrice - b.cashPrice);
    if (sort === 'price-desc') sorted.sort((a, b) => b.cashPrice - a.cashPrice);
    if (sort === 'year-desc') sorted.sort((a, b) => b.year - a.year || a.km - b.km);
    return sorted;
  }, [cars, query, bodyType, sort, filters]);

  const compared = useMemo(
    () => compareIds.map((id) => cars.find((c) => c.id === id)).filter((c): c is Car => Boolean(c)),
    [cars, compareIds],
  );

  const updateFilter = useCallback((key: keyof CatalogFilterState, value: string) => {
    setFilters((prev) => {
      if (key === 'brand') return { ...prev, brand: value, model: '', version: '' };
      if (key === 'model') return { ...prev, model: value, version: '' };
      return { ...prev, [key]: value };
    });
  }, []);

  const toggleCompare = useCallback((id: string) => {
    setCompareIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= MAX_COMPARE ? prev : [...prev, id],
    );
  }, []);

  const removeCompare = useCallback((id: string) => {
    setCompareIds((prev) => prev.filter((x) => x !== id));
  }, []);

  const clearCompare = useCallback(() => setCompareIds([]), []);

  const clearAdvancedFilters = useCallback(() => setFilters(emptyFilters), []);

  const resetFilters = useCallback(() => {
    setQuery('');
    setBodyType('Todos');
    setSort('relevance');
    setFilters(emptyFilters);
  }, []);

  const openModal = useCallback((type: ModalType, car?: Car) => {
    setActiveCar(car ?? null);
    setModalType(type);
  }, []);

  const closeModal = useCallback(() => setModalType(null), []);

  return {
    query,
    setQuery,
    bodyType,
    setBodyType,
    sort,
    setSort,
    filters,
    updateFilter,
    clearAdvancedFilters,
    filtered,
    compareIds,
    compared,
    toggleCompare,
    removeCompare,
    clearCompare,
    resetFilters,
    modalType,
    activeCar,
    openModal,
    closeModal,
  };
}
